import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { toast } from 'sonner';
import type { ApiTask } from '@/lib/adapters/tasks';

export interface TasksResponse {
  data: ApiTask[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

interface TaskFilters {
  status?: string;
  priority?: string;
  assignedTo?: string;
  clientId?: string;
  page?: number;
  limit?: number;
}

/** Tasks, optionally filtered. `enabled: false` holds the request, e.g. until a tab is shown. */
export function useTasks(filters: TaskFilters = {}, options: { enabled?: boolean } = {}) {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.priority) params.set('priority', filters.priority);
  if (filters.assignedTo) params.set('assignedTo', filters.assignedTo);
  if (filters.clientId) params.set('clientId', filters.clientId);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.limit) params.set('limit', String(filters.limit));

  const query = params.toString();
  const path = `/tasks${query ? `?${query}` : ''}`;

  return useQuery({
    queryKey: ['tasks', filters],
    queryFn: () => api.get<TasksResponse>(path),
    enabled: options.enabled ?? true,
  });
}

export function useTask(id: string | null) {
  return useQuery({
    queryKey: ['tasks', id],
    queryFn: () => api.get<ApiTask>(`/tasks/${id}`),
    enabled: !!id,
  });
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<ApiTask>) => api.post<ApiTask>('/tasks', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
      toast.success('Task created');
    },
    onError: (err: Error) => toast.error(err.message || 'Failed to create task'),
  });
}

export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Partial<ApiTask>) =>
      api.patch<ApiTask>(`/tasks/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
      toast.success('Task updated');
    },
    onError: (err: Error) => toast.error(err.message || 'Failed to update task'),
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/tasks/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
      toast.success('Task deleted');
    },
    onError: (err: Error) => toast.error(err.message || 'Failed to delete task'),
  });
}
